import type {
  ScorecardDisplay,
  ScorecardQuestionEntry,
  ScorecardRecommendation,
  ScorecardQuestionSource,
  KSSARTITopic,
  ScorecardStatus,
} from "../types";
import type { RawScorecard, RawScorecardQuestion } from "./rawTypes";
import { toScoringGuide } from "./utils";

/**
 * Compute the three aggregate scores shown on a scorecard.
 *
 * - `guideScore` averages the questions that came from the interview guide.
 * - `candidateQuestionScore` averages questions added during the interview
 *   (candidate-asked questions or ad-hoc additions).
 * - `overallScore` averages both groups together.
 *
 * Questions marked as `notAsked` or with a `null` score are excluded from
 * every average. Returns `null` for a group when no questions qualify.
 *
 * Exported for unit testing — internal callers should prefer
 * {@link toScorecardDisplay}.
 */
export function calculateScores(questions: ScorecardQuestionEntry[]): {
  guideScore: number | null;
  candidateQuestionScore: number | null;
  overallScore: number | null;
} {
  const guideQuestions = questions.filter(
    (q) => q.source === "GUIDE" && !q.notAsked && q.score !== null,
  );
  const candidateQuestions = questions.filter(
    (q) => q.source !== "GUIDE" && !q.notAsked && q.score !== null,
  );
  const allScored = questions.filter((q) => !q.notAsked && q.score !== null);

  const avg = (items: ScorecardQuestionEntry[]) =>
    items.length > 0
      ? items.reduce((sum, q) => sum + (q.score ?? 0), 0) / items.length
      : null;

  return {
    guideScore: avg(guideQuestions),
    candidateQuestionScore: avg(candidateQuestions),
    overallScore: avg(allScored),
  };
}

export function toScorecardDisplay(raw: RawScorecard): ScorecardDisplay {
  const questions: ScorecardQuestionEntry[] = (raw.questions || [])
    .map((q: RawScorecardQuestion) => ({
      // Display id = questionId — the definition key the save/remove mutations
      // address by (the GraphQL entry is a value type with no id of its own).
      id: q.questionId,
      questionText: q.questionText,
      scoringGuide: toScoringGuide(q.scoringGuide),
      order: q.order,
      score: q.score ?? null,
      notes: q.notes || "",
      notAsked: q.notAsked || false,
      source: (q.source || "GUIDE") as ScorecardQuestionSource,
      kssartiTopic: q.kssartiTopic as KSSARTITopic | undefined,
      assessmentGapKey: q.assessmentGapKey ?? undefined,
    }))
    .sort((a, b) => a.order - b.order);

  const scores = calculateScores(questions);

  return {
    id: raw.id,
    interviewId: raw.interviewId,
    user: {
      id: raw.userId || raw.user?.id || "",
      name:
        [raw.user?.firstName, raw.user?.lastName].filter(Boolean).join(" ") ||
        "Unknown",
    },
    // The query selects `guide { id }`; bare `guideId` is kept as a fallback
    // for older response shapes.
    guideId: raw.guide?.id ?? raw.guideId ?? "",
    guideTitle: raw.guideSnapshot?.title || raw.guide?.title || "",
    guideSnapshot: raw.guideSnapshot
      ? {
          guideId: raw.guideSnapshot.guideId,
          title: raw.guideSnapshot.title ?? "",
          division: raw.guideSnapshot.division ?? undefined,
          jobTitle: raw.guideSnapshot.jobTitle ?? undefined,
          status: raw.guideSnapshot.status ?? undefined,
          includeCompetencies: raw.guideSnapshot.includeCompetencies ?? true,
          competencies: (raw.guideSnapshot.competencies ?? []).map((c) => ({
            name: c.name,
            note: c.note ?? undefined,
          })),
          includeChecklist: raw.guideSnapshot.includeChecklist ?? true,
          checklist: (raw.guideSnapshot.checklist ?? []).map((c) => c.text),
          questionCount: raw.guideSnapshot.questionCount ?? 0,
          showSuggestions: raw.guideSnapshot.showSuggestions ?? false,
        }
      : null,
    status: (raw.status || "DRAFT") as ScorecardStatus,
    questions,
    recommendation: (raw.recommendation as ScorecardRecommendation) || null,
    finalComment: raw.finalComment || "",
    guideScore: raw.guideScore ?? scores.guideScore,
    candidateQuestionScore:
      raw.candidateQuestionScore ?? scores.candidateQuestionScore,
    overallScore: raw.overallScore ?? scores.overallScore,
    createdAt: raw.createdAt || "",
    updatedAt: raw.updatedAt || "",
  };
}
